import events from "@/bl-events"
import api from "@/api"
import globals from "@/utils/globals"
import * as piwik from "@/services/tracking/piwik"
import * as gtm from "@/services/tracking/gtm"
import { hasStatisticsConsent } from "@/services/cookiebot"
import { cookie } from "@/utils/cookie"
import { sendUniqEvent } from "@/services/tracking/gtm"
import { posthogIdentifyRegisteredUser } from "@/features/posthog/init"
import { setDatadogRumUser } from "@/features/datadog-rum/init"

export let isInitialized = false
const missedEvents = []
const missedBIEvents = []

export const init = () => {
  gtm.initDataLayer()

  // This enables us to listen to messages sent from iFrames, e.g. Pardot forms,
  // and thus track events from them.
  // Example of sending message from within an iFrame:
  // `window.parent.postMessage({ gtmTrackingEvent: "web-b2b-demo-request" }, "*")`
  window.onmessage = (event) => {
    if (event.data.gtmTrackingEvent) {
      gtm.sendUniqEvent({ event: event.data.gtmTrackingEvent })
    }
  }

  events.on("trackEvent", (payload) => {
    trackEvent(...payload)
  })

  api
    .mickeyMouse()
    .then((config) => {
      saveFingerprintCookie(config)
      initTrackingIfPermitted(config)

      if (config.user_id) {
        gtm.push({
          userID: config.user_id
        })
      }

      globals.store.dispatch("setup", config)
      return config
    })
    .then(posthogIdentifyRegisteredUser)
    .then(setDatadogRumUser)
    .catch((err) => {
      /* caught here to reduce noise in error tracking */
      console.error(err)
    })
}

const initTrackingIfPermitted = (config) => {
  const isTestOrDevEnvironment = ["development", "test"].indexOf(globals.env) >= 0
  // The event might have been fired before the event handler is registered. Therefore we have to check for already given permission, too.
  if (isTestOrDevEnvironment || hasStatisticsConsent()) {
    initTracking(config)
  } else {
    events.on("cookiebot:accept", () => hasStatisticsConsent() && initTracking(config))
  }
}

const saveFingerprintCookie = (config) => {
  if (location.host === "try.blinkist.com") {
    cookie("bk_c_n", config.fingerprint, 730, ".blinkist.com")
  }
}

const initTracking = (config) => {
  const categories = config.test_categories

  isInitialized = true

  piwik.setConfig(config)
  piwik.setAbTestsCustomVariable(categories)
  piwik.push("trackPageView")

  missedEvents.forEach((e) => trackEvent(...e))
  missedBIEvents.forEach((e) => trackBIEvent(...e))

  gtm.push({
    ab_tests: globals.abtests /* eslint-disable-line camelcase */,
    user_id: config.user_id /* eslint-disable-line camelcase */,
    fingerprint: config.fingerprint
  })
  sendUniqEvent({ event: "fingerprint-set" })

  if (window._bk_paq && !window._bk_paq.proxyOn) {
    window._bk_paq.turnProxyOn()
  }

  initDomEvents()
}

export const trackEvent = (...payload) => {
  if (window.tracking_module_pw && window.tracking_module_pw.events) {
    window.tracking_module_pw.events(["trackEvent", ...payload])
  } else if (isInitialized) {
    piwik.trackEvent(...payload)
  } else {
    missedEvents.push(payload)
  }
}

export const trackBIEvent = (...payload) => {
  if (window._bk_paq && window._bk_paq.trackBIEvent) {
    window._bk_paq.trackBIEvent(payload)
  } else if (isInitialized) {
    piwik.trackBIEvent(...payload)
  } else {
    missedBIEvents.push(payload)
  }
}

export const trackPageView = (path) => {
  piwik.trackPageView(path)
}

/* Track events on elements with data attributes:
- data-event-category
- data-event-action
- data-event-depth
- data-event-name */
const initDomEvents = () => {
  document.addEventListener("click", (e) => {
    const target = e.target.closest(".js-pw-link")

    if (!target) {
      return
    }

    let {
      eventCategory: category = "body-20170330-fallback_piwik_link",
      eventAction: action = "click",
      eventDepth: depth = 1,
      eventName: name
    } = target.dataset

    const eventParams = [category, `${depth}-${action}`]

    if (name) {
      eventParams.push(name)
    }

    trackEvent(...eventParams)
  })
}
